import React, { useState } from 'react';
import { motion } from 'framer-motion';
import {
    ExternalLink,
    CheckCircle,
    Clock,
    Building,
    Users,
    Code,
    Maximize2,
    Minimize2,
    RefreshCcw,
    Smartphone,
    Tablet,
    Monitor,
    Loader,
    X
} from 'lucide-react';

const LivePreview = ({ project, onClose }) => {
    const [viewportSize, setViewportSize] = useState('desktop');
    const [isLoading, setIsLoading] = useState(true);
    const [isFullscreen, setIsFullscreen] = useState(false);

    const viewportSizes = {
        mobile: 'w-[375px]',
        tablet: 'w-[768px]',
        desktop: 'w-full'
    };

    const handleRefresh = () => {
        setIsLoading(true);
        const iframe = document.getElementById('preview-frame');
        if (iframe) {
            iframe.src = iframe.src;
        }
    };

    const handleLoad = () => {
        setIsLoading(false);
    };

    return (
        <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 20 }}
            className={`fixed inset-0 bg-white/95 z-50 flex items-center justify-center ${isFullscreen ? 'p-0' : 'p-4'
                }`}
        >
            <div className="absolute top-0 left-0 right-0 bg-white border-b border-gray-800 p-4 flex items-center justify-between">
                <div className="flex items-center gap-4">
                    <h3 className="text-white font-bold">{project.title}</h3>
                    <div className="flex items-center gap-2 bg-gray-900 rounded-lg p-1">
                        <button
                            onClick={() => setViewportSize('mobile')}
                            className={`p-2 rounded-lg ${viewportSize === 'mobile' ? 'bg-blue-600 text-black' : 'text-gray-400 hover:text-white'
                                }`}
                        >
                            <Smartphone className="w-5 h-5" />
                        </button>
                        <button
                            onClick={() => setViewportSize('tablet')}
                            className={`p-2 rounded-lg ${viewportSize === 'tablet' ? 'bg-blue-600 text-black' : 'text-gray-400 hover:text-white'
                                }`}
                        >
                            <Tablet className="w-5 h-5" />
                        </button>
                        <button
                            onClick={() => setViewportSize('desktop')}
                            className={`p-2 rounded-lg ${viewportSize === 'desktop' ? 'bg-blue-600 text-black' : 'text-gray-400 hover:text-white'
                                }`}
                        >
                            <Monitor className="w-5 h-5" />
                        </button>
                    </div>
                </div>

                <div className="flex items-center gap-2">
                    <button
                        onClick={handleRefresh}
                        className="p-2 text-gray-400 hover:text-white rounded-lg"
                    >
                        <RefreshCcw className="w-5 h-5" />
                    </button>
                    <button
                        onClick={() => setIsFullscreen(!isFullscreen)}
                        className="p-2 text-gray-400 hover:text-white rounded-lg"
                    >
                        {isFullscreen ? (
                            <Minimize2 className="w-5 h-5" />
                        ) : (
                            <Maximize2 className="w-5 h-5" />
                        )}
                    </button>
                    <a
                        href={project.liveUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="p-2 text-gray-400 hover:text-white rounded-lg"
                    >
                        <ExternalLink className="w-5 h-5" />
                    </a>
                    <button
                        onClick={onClose}
                        className="p-2 text-gray-400 hover:text-white rounded-lg"
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>
            </div>

            <div className={`relative mt-16 ${viewportSizes[viewportSize]} h-[calc(100vh-5rem)] bg-white rounded-lg ${isFullscreen ? '' : 'shadow-2xl'
                }`}>
                {isLoading && (
                    <div className="absolute inset-0 flex items-center justify-center bg-gray-100">
                        <Loader className="w-8 h-8 text-blue-600 animate-spin" />
                    </div>
                )}
                <iframe
                    id="preview-frame"
                    src={project.liveUrl}
                    className="w-full h-full rounded-lg"
                    onLoad={handleLoad}
                    title={`Preview of ${project.title}`}
                />
            </div>
        </motion.div>
    );
};

const ProjectCard = ({ project, onPreview }) => {
    const fadeInUp = {
        initial: { opacity: 0, y: 60 },
        animate: { opacity: 1, y: 0 },
        transition: { duration: 0.6 }
    };

    return (
        <motion.div
            variants={fadeInUp}
            className="bg-white rounded-2xl border-2 border-black p-6 group hover:shadow-2xl transition-all duration-300"
        >
            <div className="relative mb-6">
                <img
                    src={project.image}
                    alt={project.title}
                    className="w-full h-48 object-cover rounded-xl"
                />
            </div>

            <div className="flex items-center gap-2 mb-4">
                {project.status === 'completed' ? (
                    <CheckCircle className="w-5 h-5 text-green-500" />
                ) : (
                    <Clock className="w-5 h-5 text-blue-600" />
                )}
                <span className="text-gray-600">
                    {project.status.charAt(0).toUpperCase() + project.status.slice(1)}
                </span>
            </div>

            <h3 className="text-2xl font-bold mb-2">{project.title}</h3>
            <p className="text-gray-600 mb-4">{project.description}</p>

            <div className="flex flex-wrap gap-2 mb-4">
                {project.tags.map((tag, index) => (
                    <span
                        key={index}
                        className="bg-gray-100 px-3 py-1 rounded-full text-sm text-gray-600"
                    >
                        {tag}
                    </span>
                ))}
            </div>

            <div className="flex items-center justify-between mt-4 pt-4 border-t border-gray-100">
                <div className="flex items-center gap-2">
                    {project.category === 'government' && <Building className="w-5 h-5 text-blue-600" />}
                    {project.category === 'private' && <Users className="w-5 h-5 text-blue-600" />}
                    {project.category === 'freelance' && <Code className="w-5 h-5 text-blue-600" />}
                    <span className="text-gray-600 capitalize">{project.category}</span>
                </div>
                <span className="text-gray-600">{project.completion}</span>
            </div>

            {project.status === 'completed' && project.liveUrl && (
                <div className="mt-4 flex gap-2">
                    <button
                        onClick={() => onPreview(project)}
                        className="flex-1 bg-white text-white px-4 py-2 rounded-xl font-medium hover:bg-gray-900 transition-colors"
                    >
                        Preview Website
                    </button>
                    <a
                        href={project.liveUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="bg-blue-600 p-2 rounded-xl hover:bg-yellow-500 transition-colors"
                    >
                        <ExternalLink className="w-6 h-6" />
                    </a>
                </div>
            )}
        </motion.div>
    );
};

const WorkPortfolio = () => {
    const [activeCategory, setActiveCategory] = useState('all');
    const [selectedProject, setSelectedProject] = useState(null);

    const projects = [
        {
            id: 1,
            title: "Excel Classes",
            description: "Complete overhaul of EdTech digital services platform",
            category: "Private",
            status: "completed",
            image: "/api/placeholder/600/400",
            liveUrl: "https://excelclasses.online/",
            tags: ["React", "Node.js", "PostgreSQL"],
            completion: "2024"
        },
        {
            id: 2,
            title: "E-Commerce Platform Shopezy",
            description: "Full-stack e-commerce solution for retail chain",
            category: "private",
            status: "ongoing",
            image: "/api/placeholder/600/400",
            liveUrl: "https://webordershopezy.netlify.app/",
            tags: ["Next.js", "Stripe", "MongoDB"],
            completion: "In Progress"
        },

        {
            id: 6,
            title: "Freelance Portfolio",
            description: "Custom portfolio website for creative professional of IITP",
            category: "freelance",
            status: "completed",
            image: "/api/placeholder/600/400",
            liveUrl: "https://drrishavsingh.netlify.app/",
            tags: ["React", "Tailwind", "Framer Motion"],
            completion: "2024"
        },
        {
            id: 4,
            title: "Hackathon Organization",
            description: "Custom portfolio website for Hackathon 2023",
            category: "freelance",
            status: "completed",
            image: "/api/placeholder/600/400",
            liveUrl: "https://innoquest.netlify.app/",
            tags: ["React", "Tailwind", "Framer Motion"],
            completion: "2023"
        },
        {
            id: 5,
            title: "Hawkins Cooker",
            description: "Full-stack e-commerce solution for retail chain",
            category: "freelance",
            status: "completed",
            image: "/api/placeholder/600/400",
            liveUrl: "https://hawkins-redesign.netlify.app/",
            tags: ["React", "Tailwind", "Framer Motion"],
            completion: "2024"
        },
        {
            id: 5,
            title: "Many more ......",
            // description: "Full-stack e-commerce solution for retail chain",
            // category: "freelance",
            status: "completed",
            // image: "/api/placeholder/600/400",
            // liveUrl: "https://hawkins-redesign.netlify.app/",
            tags: ["React", "Tailwind", "Framer Motion"],
            // completion: "2024"
        }
    ];

    const filterProjects = (category) => {
        if (category === 'all') return projects;
        return projects.filter(project => project.category === category);
    };

    const fadeInUp = {
        initial: { opacity: 0, y: 60 },
        animate: { opacity: 1, y: 0 },
        transition: { duration: 0.6 }
    };

    const staggerContainer = {
        animate: {
            transition: {
                staggerChildren: 0.1
            }
        }
    };

    return (
        <div className="bg-white mt-5 ">
            <motion.h2
                className="text-5xl font-bold text-black  mt-10 text-center"
                variants={fadeInUp}
                initial="initial"
                whileInView="animate"
            >
                Project Successfully <span className="text-blue-600">Delivered</span>

            </motion.h2>

            <section className="py-20 pt-15">
                <div className="container mx-auto px-6">
                    <div className="flex flex-wrap gap-4 mb-12 justify-center">
                        {['all', 'government', 'private', 'freelance'].map((category) => (
                            <button
                                key={category}
                                onClick={() => setActiveCategory(category)}
                                className={`px-6 py-3 rounded-xl font-medium transition-all ${activeCategory === category
                                    ? 'bg-blue-600 text-black'
                                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                                    }`}
                            >
                                {category.charAt(0).toUpperCase() + category.slice(1)}
                            </button>
                        ))}
                    </div>

                    <motion.div
                        variants={staggerContainer}
                        initial="initial"
                        animate="animate"
                        className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
                    >
                        {filterProjects(activeCategory).map((project) => (
                            <ProjectCard
                                key={project.id}
                                project={project}
                                onPreview={setSelectedProject}
                            />
                        ))}
                    </motion.div>
                </div>
            </section>


            {selectedProject && (
                <LivePreview
                    project={selectedProject}
                    onClose={() => setSelectedProject(null)}
                />
            )}
        </div>
    );
};

export default WorkPortfolio;